
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDataEntryForm } from './hooks/useDataEntryForm';
import { MarketTypeLocationFields } from './components/MarketTypeLocationFields';
import { ProductNameField } from './components/ProductNameField';
import { DatePickerField } from './components/DatePickerField';
import { useAuth } from '@/contexts/AuthContext';
import DataEntryDebugPanel from '@/components/debug/DataEntryDebugPanel';
import Layout from '@/components/layout/Layout';

const DataEntry: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    formData,
    date,
    isSubmitting,
    setDate,
    handleInputChange,
    handleSubmit
  } = useDataEntryForm();

  console.log('DataEntry - Current user:', user?.id, 'Role:', user?.role);

  return (
    <Layout>
      <div className="container max-w-2xl mx-auto py-8 space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Price Data Entry</h1>
            <p className="text-muted-foreground">Add new price information to the database</p>
          </div>
        </div>

        {/* Debug Panel for benchmark users */}
        {user?.role === 'benchmark' && (
          <DataEntryDebugPanel />
        )}

        {/* User Debug Info */}
        {user && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <p className="text-sm text-green-800">
                <strong>Logged in as:</strong> {user.email} (ID: {user.id}) - Role: {user.role}
              </p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>New Price Entry</CardTitle>
            <CardDescription>
              Enter price data for different market types and products. Your entries will be tracked and attributed to your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Market Type and Location - Side by Side */}
              <MarketTypeLocationFields
                marketType={formData.marketType}
                location={formData.location}
                onMarketTypeChange={(value) => handleInputChange('marketType', value)}
                onLocationChange={(value) => handleInputChange('location', value)}
              />

              {/* Product Name - Searchable */}
              <ProductNameField
                productName={formData.productName}
                onProductNameChange={(value) => handleInputChange('productName', value)}
              />

              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price">Price (ETB per kg) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  placeholder="Enter price"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  required
                />
              </div>

              {/* Date */}
              <DatePickerField
                date={date}
                onDateChange={setDate}
              />

              {/* Product Remark */}
              <div className="space-y-2">
                <Label htmlFor="productRemark">Remarks (Optional)</Label>
                <Textarea
                  id="productRemark"
                  placeholder="Any additional notes about the product or price"
                  value={formData.productRemark}
                  onChange={(e) => handleInputChange('productRemark', e.target.value)}
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full gap-2"
                disabled={isSubmitting}
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? 'Submitting...' : 'Submit Price Data'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DataEntry;
